/** Actieregister — /overleg/acties. Eén register over alle overleggen: filter op overleg, persoon, status, te laat. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, FormControlLabel, MenuItem, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import PageHeader from '../../ui-components/PageHeader';
import { overlegApi } from './overlegApi';
import { PersoonSelect, TodoLijst, bewaarActie, naam } from './OverlegDelen';

export default function Actieregister() {
  const [todos, setTodos] = useState([]);
  const [types, setTypes] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [typeId, setTypeId] = useState('');
  const [eigenaar, setEigenaar] = useState('');
  const [status, setStatus] = useState('open');
  const [teLaat, setTeLaat] = useState(false);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [mijnEmail, setMijnEmail] = useState('');

  const laad = useCallback(async () => {
    try { setTodos(await overlegApi.todos({ type_id: typeId, eigenaar, status, te_laat: teLaat })); } catch (e) { toast.error(e.message); }
  }, [typeId, eigenaar, status, teLaat]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => { overlegApi.types().then((l) => setTypes((l || []).filter((t) => !t.alleen_beheer))).catch(() => {}); overlegApi.personen().then(setPersonen).catch(() => {}); }, []);
  useEffect(() => registreerOngedaanSneltoets(), []);
  useEffect(() => { overlegApi.mijnWeek(true).then((d) => setMijnEmail(d?.mijn_email || '')).catch(() => {}); }, []);

  const toggle = async (todo) => {
    const nieuw = todo.status === 'gedaan' ? 'open' : 'gedaan';
    try { await overlegApi.todoPatch(todo.id, { status: nieuw }); } catch (e) { toast.error(e.message); return; }
    laad();
    biedOngedaanAan(nieuw === 'gedaan' ? 'Actie afgevinkt' : 'Actie heropend', async () => { await overlegApi.todoPatch(todo.id, { status: todo.status }); laad(); }, { eigenKnop: true });
  };
  const bewerk = (todo, wijziging) => bewaarActie(todo, wijziging, { mijnEmail, personen, patch: overlegApi.todoPatch, herlaad: laad });
  const nieuw = async (body) => {
    if (!typeId) { toast.error('Kies eerst een overleg'); return; }
    try { await overlegApi.todoPost({ ...body, type_id: Number(typeId) }); toast.success('Actie toegevoegd'); laad(); } catch (e) { toast.error(e.message); }
  };
  const telling = useMemo(() => ({ open: todos.filter((t) => t.status === 'open').length, teLaat: todos.filter((t) => t.te_laat).length }), [todos]);
  const perPersoon = useMemo(() => {
    const m = new Map();
    for (const t of todos) { const cur = m.get(t.eigenaar_email) || { open: 0, teLaat: 0 }; if (t.status === 'open') cur.open += 1; if (t.te_laat) cur.teLaat += 1; m.set(t.eigenaar_email, cur); }
    return [...m.entries()].sort((a, b) => b[1].open - a[1].open);
  }, [todos]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="fact_check" title="Actieregister" subtitle="Alle acties uit alle overleggen: eigenaar, deadline, status, en waar ze uit volgden" accent={{ bg: '#e0e7ff', color: '#4338ca' }}
        rightActions={<Button variant="contained" disableElevation size="small" onClick={() => setNieuwOpen((v) => !v)} sx={{ textTransform: 'none', fontWeight: 700 }}>＋ Actie</Button>} />
      <Paper variant="outlined" sx={{ p: 1.25, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField select size="small" label="Overleg" value={typeId} onChange={(e) => setTypeId(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">Alle overleggen</MenuItem>
          {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.naam}</MenuItem>)}
        </TextField>
        <PersoonSelect personen={personen} value={eigenaar} onChange={setEigenaar} allowLeeg label="Eigenaar" />
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="open">Open</MenuItem><MenuItem value="gedaan">Gedaan</MenuItem><MenuItem value="vervallen">Vervallen</MenuItem><MenuItem value="alle">Alle</MenuItem>
        </TextField>
        <FormControlLabel sx={{ m: 0 }} control={<Switch size="small" checked={teLaat} onChange={(e) => setTeLaat(e.target.checked)} />} label={<Typography variant="caption">alleen te laat</Typography>} />
        <Box sx={{ flex: 1 }} />
        <Chip label={`${telling.open} open`} sx={{ fontWeight: 700 }} />
        {telling.teLaat > 0 && <Chip label={`${telling.teLaat} te laat`} sx={{ fontWeight: 800, bgcolor: '#fee2e2', color: '#991b1b' }} />}
      </Paper>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <TodoLijst todos={todos} personen={personen} onToggle={toggle} onBewerk={bewerk} onNieuw={nieuwOpen ? nieuw : undefined} />
          {nieuwOpen && !typeId && <Typography variant="caption" color="error">Kies boven een overleg om een actie aan toe te voegen.</Typography>}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', mb: 1 }}>Per persoon</Typography>
          <Stack spacing={0.5}>
            {perPersoon.map(([e, c]) => (
              <Stack key={e} direction="row" justifyContent="space-between" onClick={() => setEigenaar(e)} sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{naam(e)}</Typography>
                <Typography sx={{ fontSize: 13, color: c.teLaat ? '#991b1b' : '#64748b', fontWeight: c.teLaat ? 800 : 500 }}>{c.open}{c.teLaat ? ` · ${c.teLaat} te laat` : ''}</Typography>
              </Stack>
            ))}
            {perPersoon.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Niets in deze selectie.</Typography>}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
